//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import $ from "jquery";

import {
  IApprovedItemsRightsResponse,
  IDeletePinnedItemsRequest,
  IGallery,
  IGetGalleriesResponse,
  IGetGalleryItemsResponse,
  IGetSitesResponse,
  IMovePinnedItemsRequest,
  IPostAutoApprovalConfigRequest,
  ISelfServeGallery,
  ISetPinnedItemsRequest,
  ISetRightsManagementBulkRequest,
} from '@apis/galleries/galleriesApiModels';
import { IAutoApprovalConfiguration } from '@galleries/models/AutoApprovalConfiguration';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import GalleryDisplayName from '@galleries/models/GalleryDisplayName';
import { IReel } from '@galleries/models/IReel';
import {
  IGalleryItem,
  InstaShopAutoApprovalSources,
} from '@galleries/models/galleriesStateModel';
import { FanreelCustomizationSortValues } from '@legacy-components/fanreel-self-serve-customization/fanreel-customization-types';
import Cuid from '@models/Cuid';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import { ExperienceLayoutType } from '@models/ExperienceLayoutType';
import ReelId from '@models/ReelId';
import { gatewayFetch } from '@utils/sessionFetch';

const GALLERIES_BASE_URL = '/api/v1/galleries';
const JOBS_BASE_URL = '/api/v1/jobs';

function galleriesFetch(url: string, init?: RequestInit): Promise<Response> {
  return gatewayFetch('experience_gateway', url, init);
}

/**
 * Creates a new gallery of the given type with the given display name
 *
 * @param containerType
 * @param displayName
 *
 * @returns New gallery as a promise
 */
export const createGallery = async (
  containerType: ExperienceContainerType,
  displayName: GalleryDisplayName,
  instashopLocation?: ExperienceContainerType,
): Promise<ISelfServeGallery> => {
  const params = {
    containerType,
    customizationOptions: {
      sort:
        containerType === ExperienceContainerType.INSTASHOP
          ? FanreelCustomizationSortValues.LATEST
          : FanreelCustomizationSortValues.MODERATION,
    },
    displayName,
    layout: ExperienceLayoutType.CAROUSEL,
    instashopLocation,
  };

  const res = await galleriesFetch(GALLERIES_BASE_URL, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return res.json();
};

/**
 * Gets the galleries associated with the current client
 *
 * @returns List of galleries as a promise
 */
export const getGalleries = async (
  init?: RequestInit,
): Promise<IGetGalleriesResponse> => {
  const res = await galleriesFetch(GALLERIES_BASE_URL, init);
  return (await res.json()) as IGetGalleriesResponse;
};

/**
 * Gets the custom galleries associated with the current client
 *
 * @returns List of galleries as a promise
 */
export const getCustomGalleries = async (
  init?: RequestInit,
): Promise<IGetGalleriesResponse> => {
  const res = await galleriesFetch(`${GALLERIES_BASE_URL}/custom`, init);
  return res.json();
};

/**
 * Gets the sites associated with the current client
 *
 * @param isCustom
 *  if true, return only custom sites.
 *  if false, return only self-serve sites.
 *  if undefined/not specified, return all sites.
 *
 * @returns List of sites as a promise
 */
export const getSites = async (
  isCustom?: boolean,
): Promise<IGetSitesResponse> => {
  const paramsObj = {
    isCustom,
  };

  const params = $.param(paramsObj);
  const res = await galleriesFetch(`${GALLERIES_BASE_URL}/sites?${params}`);
  return (await res.json()) as IGetSitesResponse;
};

/**
 * Gets the gallery items associated with the given container that match
 * the current search text and publication status filter per the given
 * pagination (offset and limit) options
 *
 * @param containerId
 * @param offset
 * @param limit
 * @param searchText
 * @param statusFilter
 * @param dateRange
 *
 * @returns List of gallery items as a promise
 */
export const getGalleryItems = async (
  containerId: GalleryContainerId,
  offset: number,
  limit: number,
  searchText?: string,
  statusFilter?: string,
  dateRange?: string,
): Promise<IGetGalleryItemsResponse> => {
  const paramsObj = {
    containerId,
    offset,
    limit,
    ...(searchText && { searchText }),
    ...(statusFilter && { statusFilter }),
    ...(dateRange && { dateRange }),
  };

  const params = $.param(paramsObj);

  const res = await galleriesFetch(
    `${GALLERIES_BASE_URL}/${containerId}/items?${params}`,
  );
  return (await res.json()) as IGetGalleryItemsResponse;
};

/**
 * Gets the gallery item associated with the given full cuid
 *
 * @param cuid
 *
 * @returns Gallery item as a promise
 */
export const getGalleryItemByCuid = async (
  containerId: GalleryContainerId,
  cuid: Cuid,
): Promise<IGalleryItem> => {
  const res = await galleriesFetch(
    `${GALLERIES_BASE_URL}/${containerId}/items/${cuid}`,
  );
  return (await res.json()) as IGalleryItem;
};

/**
 * Updates the display name of a gallery
 *
 * @param containerId
 * @param newDisplayName
 *
 * @returns Gallery
 */
export const updateGalleryName = async (
  containerId: GalleryContainerId,
  newDisplayName: string,
): Promise<IGallery> => {
  const res = await galleriesFetch(`${GALLERIES_BASE_URL}/${containerId}`, {
    method: 'PUT',
    body: JSON.stringify({
      newDisplayName,
    }),
  });

  return (await res.json()) as IGallery;
};

/**
 * Deletes a gallery
 *
 * @param containerId
 *
 * @returns An empty promise
 */
export const deleteGallery = async (
  containerId: GalleryContainerId,
): Promise<Response> => {
  return await galleriesFetch(`${GALLERIES_BASE_URL}/${containerId}`, {
    method: 'DELETE',
  });
};

/**
 * Gets pinned media for a gallery
 *
 * @param containerId: The gallery's container id
 *
 * @returns A list of pinned gallery items
 */
export const getPinnedItems = async (
  containerId: GalleryContainerId,
  offset = 0,
  limit = 30,
): Promise<IGetGalleryItemsResponse> => {
  const res = await galleriesFetch(
    `${GALLERIES_BASE_URL}/${containerId}/pinned-items?offset=${offset}&limit=${limit}`,
  );
  return (await res.json()) as IGetGalleryItemsResponse;
};

/**
 * Set pinned media for a gallery
 *
 * @param containerId: The gallery's container id
 * @param cuids: Cuids to be pinned
 *
 * @returns An empty promise
 */
export const setPinnedMedia = async (
  containerId: GalleryContainerId,
  pinnedCuids: Cuid[],
): Promise<void> => {
  const requestBody: ISetPinnedItemsRequest = {
    cuids: pinnedCuids,
  };

  await galleriesFetch(`${GALLERIES_BASE_URL}/${containerId}/pinned-items`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
};

/**
 * Update positions for a gallery's pinned media
 *
 * @param containerId: The gallery's container id
 * @param movingCuid: The cuid being moved
 * @param targetIndex: The final index in the list of pinned items that the cuid is being moved to
 */
export const updatePinnedMedia = async (
  containerId: GalleryContainerId,
  movingCuid: Cuid,
  targetPosition: number,
): Promise<void> => {
  const putPinnedItemsRequestBody: IMovePinnedItemsRequest = {
    movingCuid,
    targetPosition,
  };

  await galleriesFetch(`${GALLERIES_BASE_URL}/${containerId}/pinned-items`, {
    method: 'PUT',
    body: JSON.stringify(putPinnedItemsRequestBody),
  });
};

/**
 * Unpin Item From a Gallery
 *
 * @param containerId: The gallery's container id
 * @param cuid: The item to un-pin
 *
 * @returns An empty promise
 */
export const unpinItems = async (
  containerId: GalleryContainerId,
  cuids: Cuid[],
): Promise<void> => {
  const requestBody: IDeletePinnedItemsRequest = { cuids };

  await galleriesFetch(`${GALLERIES_BASE_URL}/${containerId}/pinned-items`, {
    method: 'DELETE',
    body: JSON.stringify(requestBody),
  });
};

/**
 * Changes an Auto-Approval Configuration's auto approval strategy
 *
 * @param configId The id of the auto approval configuration to update
 * @param autoApprovalType The new auto approval strategy for that configuration
 */
export const updateAutoApprovalConfigStrategy = async (
  configId: number,
  autoApprovalType: InstaShopAutoApprovalSources,
): Promise<{ data: IAutoApprovalConfiguration }> => {
  const res = await galleriesFetch(
    `${GALLERIES_BASE_URL}/reel-auto-approval-configs/${configId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        autoApprovalStrategy: autoApprovalType,
      }),
    },
  );
  return res.json();
};

/**
 * Deletes an Auto-Approval Configuration
 *
 * @param configId The id of the auto approval configuration to delete
 */
export const deleteAutoApprovalConfig = async (
  configId: number,
): Promise<void> => {
  await galleriesFetch(
    `${GALLERIES_BASE_URL}/reel-auto-approval-configs/${configId}`,
    {
      method: 'DELETE',
    },
  );
};

/**
 * Creates a new reel auto approval configuration
 *
 * @param data Options for the new approval configuration
 */
export const createAutoApprovalConfig = async (
  data: IPostAutoApprovalConfigRequest,
): Promise<{ data: IAutoApprovalConfiguration }> => {
  const res = await galleriesFetch(
    `${GALLERIES_BASE_URL}/reel-auto-approval-configs`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
  return res.json();
};

/**
 * Gets a gallery's auto-approval configurations
 *
 * @param reelId: The reelId of a gallery
 */
export const getAutoApprovalConfigs = async (
  reelId: ReelId,
): Promise<{ data: IAutoApprovalConfiguration[] }> => {
  const res = await galleriesFetch(
    `${GALLERIES_BASE_URL}/reel-auto-approval-configs/${reelId}`,
  );
  return res.json();
};

/**
 * Runs a backfill job for a corresponding auto approval configuration
 *
 * @param configId
 */
export const backfillAutoApprovalConfig = async (
  configId: number,
): Promise<void> => {
  await galleriesFetch(
    `${GALLERIES_BASE_URL}/reel-auto-approval-configs/${configId}/backfill`,
    {
      method: 'POST',
    },
  );
};

/**
 * Approves first N items from an auto approval configuration
 *
 * @param configId
 * @param numItems
 */
export const approveNItems = async (
  configId: number,
  numItems: number,
): Promise<Response> => {
  return galleriesFetch(
    `${GALLERIES_BASE_URL}/reel-auto-approval-configs/${configId}/approve-n-items`,
    {
      method: 'POST',
      body: JSON.stringify({
        numItems,
      }),
    },
  );
};

/**
 * Gets the number of items approved to each of a client's galleries
 *  that need rights.
 */
export const getNeedsRightsItems = async (): Promise<
  Record<ReelId, number>
> => {
  const resp = await galleriesFetch(
    `${GALLERIES_BASE_URL}/approved-items-without-rights`,
  );
  const parsedResp: IApprovedItemsRightsResponse = await resp.json();
  return parsedResp.data as Record<ReelId, number>;
};

/**
 * Changes the rights-management setting for a reel
 */
export const setRightsManagement = async ({
  isRightsManaged,
  reelIds,
  skipRightsOnApprovedItems,
}: ISetRightsManagementBulkRequest): Promise<IReel[]> => {
  const resp = await galleriesFetch(
    `${GALLERIES_BASE_URL}/set-rights-management-bulk`,
    {
      method: 'PUT',
      body: JSON.stringify({
        isRightsManaged,
        skipRightsOnApprovedItems,
        reelIds,
      }),
    },
  );

  const jsonRes = await resp.json();
  return jsonRes.data;
};

/**
 * Check whether or not a skip-rights job is running for
 * a given reel id
 */
export const isSkipRightsRunning = async (reelId: ReelId): Promise<boolean> => {
  const resp = await galleriesFetch(
    `${JOBS_BASE_URL}/is-skip-rights-job-running/${reelId}`,
  );

  const jsonRes = await resp.json();
  return jsonRes.data;
};
